import {addSortToQuery, getSortFromQuery} from '@github-ui/dashboard-sorting-dropdown/query'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {hpcAttrs} from '@github-ui/hpc'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {Link as PreloadableLink, matchPath, useLocation, useNavigation} from '@github-ui/react-router'
import {PullsList} from '@github-ui/shared-dashboard-lists/pulls/PullsList'
import type {EnrichedPullListItem} from '@github-ui/shared-dashboard-lists/pulls/types/list-types'
import {SharedDashboardBlankslate} from '@github-ui/shared-dashboard-lists/shared/SharedDashboardBlankslate'
import {addStateToQuery, getStateFromQuery} from '@github-ui/shared-dashboard-surface/helpers/open-closed-toggle'
import {
  addAssigneeFilterToQuery,
  addAuthorFilterToQuery,
  addLabelFilterToQuery,
} from '@github-ui/shared-dashboard-surface/helpers/query-filters'
import {useScreenReaderAnnouncements} from '@github-ui/shared-dashboard-surface/hooks/use-screen-reader-announcements'
import {useUpdateCompactModeSetting} from '@github-ui/shared-dashboard-surface/hooks/use-update-compact-mode-setting'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {Pagination} from '@primer/react'

import {useRepoPullsDashboardData, useRepoPullsSearch} from '../hooks/use-repo-pulls-dashboard'
import {repoPullsDashboardLayoutRoute} from './repo-pulls-dashboard-layout-route'
import {useRepoPullsQuickFilterItems} from './repo-pulls-quick-filters/use-repo-pulls-quick-filter-actions'

const REPO_PULLS_DEFAULT_SORTING_KEY = 'created'

function isSameLocation(a: {pathname: string; search: string}, b: {pathname: string; search: string}) {
  return a.pathname === b.pathname && a.search === b.search
}

function addRepoStateToQuery(query: string, state: string) {
  const queryWithoutLegacyState = query
    .split(' ')
    .filter(term => term !== 'is:open' && term !== 'is:closed')
    .join(' ')
  return addStateToQuery(queryWithoutLegacyState, state)
}

function getRepoStateFromQuery(query: string) {
  const state = getStateFromQuery(query)
  if (state) return state
  if (query.split(' ').includes('is:open')) return 'open'
  if (query.split(' ').includes('is:closed')) return 'closed'
  return ''
}

function getPullRequestTitleLinkProps(pull: EnrichedPullListItem) {
  try {
    if (new URL(pull.permalink, ssrSafeLocation.origin).origin !== ssrSafeLocation.origin) return undefined
  } catch {
    return undefined
  }

  return {as: PreloadableLink, to: pull.permalink, preload: 'intent' as const, reloadDocument: false}
}

export function RepoPullsDashboardContent() {
  const location = useLocation()
  const navigation = useNavigation()
  const isNavigating = Boolean(
    navigation.location &&
    matchPath('/:owner/:repo/pulls/*', navigation.location.pathname) &&
    !isSameLocation(navigation.location, location),
  )

  return (
    <ErrorBoundary
      boundaryName="RepoPullsDashboardContent"
      fallback={
        <div className="container-xl px-3 px-md-4 px-lg-5 tmp-my-3">
          <SharedDashboardBlankslate dashboardType="pull-requests" variant="error" />
        </div>
      }
    >
      <RepoPullsDashboardPage isNavigating={isNavigating} />
    </ErrorBoundary>
  )
}

function RepoPullsDashboardPage({isNavigating}: {isNavigating: boolean}) {
  const {data: layoutPayload} = useRouteQuery(repoPullsDashboardLayoutRoute, 'mainQuery')
  const {error, isDeferredLoading, pageInfo, pulls, stateCounts} = useRepoPullsDashboardData()
  const {currentPage, fetchedQuery, fetchResults, getPageHref, owner, repo} = useRepoPullsSearch()
  const compactMode = layoutPayload?.compactMode ?? false
  const showRepositoryNavigation = Boolean(layoutPayload?.actions?.labels)
  const quickFilterItems = useRepoPullsQuickFilterItems({
    owner: owner ?? '',
    repo: repo ?? '',
    query: fetchedQuery,
    onQueryChange: query => fetchResults({q: query}),
    showLabels: showRepositoryNavigation,
    showMilestones: showRepositoryNavigation,
    showProjects: showRepositoryNavigation,
  })

  const {emptyListState} = useScreenReaderAnnouncements(
    'pull-requests',
    isDeferredLoading,
    currentPage,
    fetchedQuery,
    REPO_PULLS_DEFAULT_SORTING_KEY,
    pageInfo.totalCount,
    pageInfo.totalPages,
    error ?? undefined,
  )
  const {updateCompactModeSetting} = useUpdateCompactModeSetting(compactMode, 'dashboard_surface_compact_mode', {
    persistAsPullsPreference: true,
  })

  function handleFilterSelect(type: 'repo' | 'author' | 'label' | 'assignee', value: string) {
    let nextQuery = fetchedQuery
    switch (type) {
      case 'repo':
        return
      case 'author':
        nextQuery = addAuthorFilterToQuery(fetchedQuery, value)
        break
      case 'label':
        nextQuery = addLabelFilterToQuery(fetchedQuery, value)
        break
      case 'assignee':
        nextQuery = addAssigneeFilterToQuery(fetchedQuery, value)
        break
    }
    fetchResults({q: nextQuery})
  }

  return (
    <div>
      <div className="tmp-my-3" {...hpcAttrs()}>
        {error ? (
          <SharedDashboardBlankslate dashboardType="pull-requests" variant="error" />
        ) : (
          <>
            <PullsList
              pulls={pulls}
              pageInfo={pageInfo}
              compactMode={compactMode}
              onCompactModeChange={updateCompactModeSetting}
              onSortingItemSelect={sort => fetchResults({q: addSortToQuery(fetchedQuery, sort)})}
              onStateFilterSelect={state => fetchResults({q: addRepoStateToQuery(fetchedQuery, state)})}
              sortingItemSelected={getSortFromQuery(fetchedQuery)}
              stateItemSelected={getRepoStateFromQuery(fetchedQuery)}
              stateCounts={stateCounts}
              emptyListState={emptyListState}
              onFilterSelect={handleFilterSelect}
              isLoading={isNavigating}
              isDeferredLoading={isDeferredLoading}
              isAgentAttributionEnabled={isFeatureEnabled('agent_author_search_expansion_ui_pulls')}
              defaultSortingKey={REPO_PULLS_DEFAULT_SORTING_KEY}
              showRepository={false}
              actionBarItems={owner && repo ? quickFilterItems : undefined}
              getTitleLinkProps={getPullRequestTitleLinkProps}
            />
            {pageInfo.totalPages > 1 ? (
              <Pagination
                pageCount={pageInfo.totalPages}
                currentPage={pageInfo.currentPage}
                hrefBuilder={getPageHref}
                renderPage={({key, number, href, ...props}) => (
                  <PreloadableLink key={key} {...props} to={href ?? getPageHref(number)} preload="intent" />
                )}
                onPageChange={(event, page) => {
                  event.preventDefault()
                  fetchResults({q: fetchedQuery, page})
                }}
              />
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}
